import React, { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useParams } from 'react-router-dom';

import { useSettings } from '../context/SettingsContext'
import { Nav } from './partials/Nav';
import { API_URL } from './Constants';

const TransactionDetail = () => {
  const settings = useSettings();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [transaction, setTransaction] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = 'Transaction Detail | ValParx Admin'
  }, []);

  useEffect(() => {
    if (settings.userToken && id) {
      fetchTransactionDetail();
    }
  }, [settings.userToken, id]);

  const fetchTransactionDetail = () => {
    setLoading(true);
    setError(null);
    
    const token = settings.userToken;
    const myHeaders = new Headers();
    myHeaders.append("vp-session-token", token);
    myHeaders.append("Content-Type", "application/x-www-form-urlencoded");

    const urlencoded = new URLSearchParams();
    urlencoded.append("transaction_id", id);

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: urlencoded,
      redirect: 'follow'
    };

    fetch(API_URL + "/gettransactiondetail", requestOptions)
      .then(response => response.json())
      .then(result => {
        if (result.code === 200) {
          setTransaction(result.transaction);
        } else {
          setError(result.message || 'Failed to fetch transaction details');
        }
        setLoading(false);
      })
      .catch(error => {
        console.error('error', error);
        setError('Failed to fetch transaction details');
        setLoading(false);
      });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '1970-01-01 00:00:00' || dateString === '0000-00-00 00:00:00') {
      return 'N/A';
    }
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <Nav />
        <div className="text-center">
          <FontAwesomeIcon icon="fa-solid fa-spinner" className='text-5xl text-accent fa-spin' />
          <p className="mt-4 text-gray-600">Loading transaction details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='min-h-screen flex items-start justify-center py-12 px-4 sm:px-6 lg:px-8'>
        <Nav />
        <div className="max-w-7xl w-full space-y-8 pt-20">
          <Link to="/reports/transactions" className="text-accent hover:text-accent-dark mb-4 inline-flex items-center">
            <FontAwesomeIcon icon="fa-arrow-left fa-solid" className='mr-2 text-sm' />
            Back to Transaction Report
          </Link>
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!transaction) {
    return null;
  }

  return (
    <div className='min-h-screen flex items-start justify-center py-12 px-4 sm:px-6 lg:px-8'>
      <Nav />
      <div className="max-w-4xl w-full space-y-8 pt-20">
        <div className='flex justify-between items-center mb-8'>
          <div>
            <Link to="/reports/transactions" className="text-accent hover:text-accent-dark mb-2 inline-flex items-center">
              <FontAwesomeIcon icon="fa-arrow-left fa-solid" className='mr-2 text-sm' />
              Back to Transaction Report
            </Link>
            <h2 className='mb-0'>Transaction Details</h2>
            <p className="text-gray-600 text-sm mt-1">Transaction ID: {transaction.id}</p>
          </div>
        </div>

        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          {/* Status Header */}
          <div className={`px-6 py-4 border-b border-gray-200 ${
            transaction.status === 0 ? 'bg-green-50' : 
            transaction.status === 1 ? 'bg-blue-50' : 
            transaction.status === 2 ? 'bg-yellow-50' : 
            transaction.status === 3 ? 'bg-purple-50' : 
            'bg-orange-50'
          }`}>
            <h3 className="text-lg font-semibold text-gray-900">
              Status: <span className={`${
                transaction.status === 0 ? 'text-green-700' : 
                transaction.status === 1 ? 'text-blue-700' : 
                transaction.status === 2 ? 'text-yellow-700' : 
                transaction.status === 3 ? 'text-purple-700' : 
                'text-orange-700'
              }`}>{transaction.status_text}</span>
            </h3>
          </div>

          {/* Transaction Details */}
          <div className="px-6 py-6 space-y-6">
            {/* Lot Information */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3 border-b pb-2">Lot Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Lot Name</p>
                  <p className="text-base font-medium text-gray-900">{transaction.lot_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Stall Number</p>
                  <p className="text-base font-medium text-gray-900">{transaction.stall_number || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Customer Information */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3 border-b pb-2">Customer Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Customer Name</p>
                  <p className="text-base font-medium text-gray-900">{transaction.customer_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Company Name</p>
                  <p className="text-base font-medium text-gray-900">{transaction.company_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Phone Number</p>
                  <p className="text-base font-medium text-gray-900">{transaction.customer_phone || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="text-base font-medium text-gray-900">{transaction.customer_email || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Vehicle Information */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3 border-b pb-2">Vehicle Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Make</p>
                  <p className="text-base font-medium text-gray-900">{transaction.vehicle_make || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Color</p>
                  <p className="text-base font-medium text-gray-900">{transaction.vehicle_color || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">License Plate</p>
                  <p className="text-base font-medium text-gray-900">{transaction.vehicle_plate || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Parking Details */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3 border-b pb-2">Parking Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Key Tag</p>
                  <p className="text-base font-medium text-gray-900">{transaction.key_tag || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Parker Hangtag</p>
                  <p className="text-base font-medium text-gray-900">{transaction.parker_hangtag || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Driver (Check-in)</p>
                  <p className="text-base font-medium text-gray-900">{transaction.driver || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Closing Driver (Check-out)</p>
                  <p className="text-base font-medium text-gray-900">{transaction.closing_driver || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Payment Information */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3 border-b pb-2">Payment Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Payment Method</p>
                  <p className="text-base font-medium text-gray-900">{transaction.payment_method_text}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Charge Amount</p>
                  <p className="text-base font-medium text-gray-900">{formatCurrency(transaction.charge_amount)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Fee Collected</p>
                  <p className="text-base font-medium text-gray-900">{formatCurrency(transaction.fee_collected)}</p>
                </div>
              </div>
            </div>

            {/* Timestamps */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3 border-b pb-2">Timeline</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Time Created</p>
                  <p className="text-base font-medium text-gray-900">{formatDate(transaction.time_created)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Last Updated</p>
                  <p className="text-base font-medium text-gray-900">{formatDate(transaction.last_updated)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Time Closed</p>
                  <p className="text-base font-medium text-gray-900">{formatDate(transaction.time_closed)}</p>
                </div>
              </div>
            </div>

            {/* Entry Key */}
            <div>
              <h4 className="text-md font-semibold text-gray-900 mb-3 border-b pb-2">System Information</h4>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Entry Key</p>
                  <p className="text-base font-medium text-gray-900 font-mono bg-gray-50 p-2 rounded">{transaction.entry_key}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TransactionDetail;
